import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell
} from '@/components/ui/table';
import { Loader2, PlusIcon, List } from 'lucide-react';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';

interface Record {
  recordId: string;
  englishName: string | null;
  scientificName: string | null;
  speciesId: string | null;
  dtm: string;
  count: number | null;
  lat: number | null;
  lng: number | null;
  note: string | null;
  locationNote: string | null;
  createdAt: string;
  updatedAt: string;
}
import { useNavigate } from 'react-router';
import { PageSectionTitle } from '../nav/PageSectionTitle';
import { useOrgContext } from '@/contexts/OrgContext';

export const LatestOrgRecords: React.FC<{ orgId: string }> = ({ orgId }) => {
  const navigate = useNavigate();
  const { orgRole } = useOrgContext();
  const { data: records = [], isLoading, isError } = useQuery<Record[]>({
    queryKey: ['orgRecords'],
    queryFn: async () => {
      const res = await fetch(`/api/org/${orgId}/records`);
      if (!res.ok) throw new Error('Failed to fetch organization records');
      return res.json();
    },
  });

  const formatDate = (dateString: string) => {
    const d = new Date(dateString);
    return (
      d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    );
  };

  if (isLoading) return (
    <div className="flex justify-center p-4">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );

  if (isError) return (
    <div className="bg-red-50 p-4 rounded-md text-red-800">
      Failed to load organization records.
    </div>
  );

  return (
    <>
      <div className="flex flex-row justify-between items-center mb-6">
        <h2 className="text-xl font-bold tracking-tight">Latest Records</h2>

        <div className="flex flex-row gap-2">
          <Link to={`/app/org/${orgId}/records`}>
            <Button variant="outline">
              <List className="h-4 w-4" />
              <span className="hidden md:inline">View All</span>
            </Button>
          </Link>
          {orgRole === "ADMIN" && (
            <Link to={`/app/org/${orgId}/records/add`}>
              <Button variant="outline">
                <PlusIcon className="h-4 w-4" />
                <span className="hidden md:inline">Add New Record</span>
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Card layout for mobile */}
      <div className="block md:hidden space-y-4">
        {records.slice(0, 10).map((record) => (
          <div key={record.recordId} className="rounded-lg shadow bg-white/90 p-4 flex flex-col gap-2" onClick={() => navigate(`/app/org/${orgId}/record/${record.recordId}`)}>
            <div>
              <div className="font-medium text-lg">{record.englishName || 'Unknown Species'}</div>
              <div className="text-sm italic text-muted-foreground">{record.scientificName}</div>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{record.note || '-'}</p>
              {record.locationNote && (
                <p className="text-sm text-muted-foreground mt-1">{record.locationNote}</p>
              )}
            </div>
            <div className="text-xs text-muted-foreground mt-2">{formatDate(record.dtm)}</div>
          </div>
        ))}
      </div>
      {/* Table layout for md+ */}
      <div className="hidden md:block overflow-auto max-h-[60vh] w-full min-w-0">
        <Table className="w-full min-w-0">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Note</TableHead>
              <TableHead>Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {records.slice(0, 10).map((record) => (
              <TableRow key={record.recordId}>
                <TableCell>
                  <div className="font-medium">{record.englishName || 'Unknown Species'}</div>
                  <div className="text-sm italic text-muted-foreground">{record.scientificName}</div>
                </TableCell>
                <TableCell>
                  <p className="text-sm text-muted-foreground line-clamp-2">{record.note || '-'}</p>
                  {record.locationNote && (
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{record.locationNote}</p>
                  )}
                </TableCell>
                <TableCell>{formatDate(record.dtm)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

    </>
  );
};
